import axios, { AxiosRequestConfig, Method } from "axios"
import { refreshToken } from "../utility"
import { errorFilledToastOptions, UNAUTHORIZED_ERROR_NAME } from "../constants"
import { Action, Dispatch } from "redux"
import { toast } from "react-toastify"
import { AUTH_ACTION } from "../actions/loginActions"

// Global interceptor to suppress canceled request errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.message === "canceled" || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
      // Silently ignore canceled requests
      return new Promise(() => {})
    }
    return Promise.reject(error)
  }
)

const createRequest = (path: string) => {
  const instance = axios.create({
    baseURL: `${process.env.REACT_APP_BASE_URL}${path}`,
    withCredentials: true
  })
  // Add interceptor to suppress canceled request errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.message === "canceled" || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
        return new Promise(() => {})
      }
      return Promise.reject(error)
    }
  )
  return instance
}

export const controllers: Record<string, AbortController> = {}

const createAuthorizedRequest = async (path: string, config?: AxiosRequestConfig, abortable = false) => {
  const error = new Error()
  error.name = UNAUTHORIZED_ERROR_NAME
  if (!(await refreshToken())) throw error

  const controllerId = path.split("?")[0]
  const controller = controllers[controllerId]

  if (abortable) {
    if (controller) {
      controller.abort()
    }
    controllers[controllerId] = new AbortController()
  }

  return axios({
    ...config,
    url: `${process.env.REACT_APP_BASE_URL}${path}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "affiliate-user-id": localStorage.getItem("appliedAffiliate") ?? ""
    },
    withCredentials: true,
    ...(abortable && { signal: controllers[controllerId].signal })
  })
}

export const authLogin = createRequest("/login")
export const authRefresh = createRequest("/refresh")
export const codeValidation = createRequest("/code-validation")
export const publicInvitation = createRequest("/public/track-share-holder-invitation/approve_invitation")
export const smartLinks = createRequest(`/public/smart-link`)
export const spotify = createRequest(`/public/spotify`)
export const publicApi = createRequest(`/public/api`)
export const authLogout = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/logout${path}`, { method, data })
export const me = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/me${path}`, { method, data })
export const users = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/users${path}`, { method, data }, true)
export const paymentMethods = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/payment-method${path}`, { method, data })
export const releases = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases${path}`, { method, data }, false)
export const marketing = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/marketing${path}`, { method, data }, false)
export const tracks = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/track${path}`, { method, data }, true)
export const trackShareHolders = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/track-share-holder${path}`, { method, data }, true)
export const youtubeVideoShareHolders = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/youtube-video-share-holder${path}`, { method, data }, true)
export const summaries = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/summary${path}`, { method, data })
export const trends = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/trends${path}`, { method, data })
export const userPayments = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/payments/user-payment-history${path}`, { method, data })
export const currencyExchange = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/currency-exchange${path}`, { method, data })
export const completeShareHolders = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/track-share-holder/complete_share_holders/${path}`, { method, data }, true)
export const collaboratorInvitation = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/track-share-holder-invitation${path}`, { method, data }, true)
export const schedulers = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/scheduler${path}`, { method, data })
export const assistant = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/assistant${path}`, { method, data }, true)
export const affiliate = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/affiliate${path}`, { method, data }, true)
export const userRegistration = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/user-registration${path}`, { method, data }, true)
export const notifications = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/notifications${path}`, { method, data }, true)
export const announcements = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/announcements${path}`, { method, data }, true)
export const trackPlaylist = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/insights/track-playlist${path}`, { method, data }, true)

export const handleAuthorizedRequest = async (
  callback: () => Promise<any>,
  casualErrorMessage: string,
  dispatch: Dispatch<Action> = null
) => {
  try {
    return await callback()
  } catch (e) {
    if (e.name === UNAUTHORIZED_ERROR_NAME || e.response?.data?.action === "logout") {
      console.error("Incorrect authorization details. Logging out...")
      if (dispatch) {
        dispatch({
          type: AUTH_ACTION.LOGOUT
        })
      } else {
        localStorage.setItem("user", "{}")
        localStorage.setItem("token", "")
      }
      window.location.pathname = "/login"
    }
    if (e.message === "canceled" || e.code === "ERR_CANCELED" || e.name === "CanceledError") {
      // Request was aborted, silently ignore
      return
    }
    console.error(e)
    toast.error(casualErrorMessage, errorFilledToastOptions)
  }
}
